import { useState } from "react";
import styles from "./DealerAnalytics.module.scss";
import { BarList, Funnel, Legend, TrendChart } from "./charts";
import {
  Card, Delta, Empty, ReportFrame, Segmented, SortTable, StatTile,
  fmtCompact, fmtDate, fmtDayKey, fmtHours, fmtNum, fmtUsd, periodLabels, useReport, waitedFor,
} from "./shared";

const TREND_METRICS = [
  { value: "views", label: "Car views" },
  { value: "visitors", label: "Visitors" },
  { value: "searches", label: "Searches" },
  { value: "saves", label: "Saves" },
  { value: "leads", label: "Leads" },
  { value: "inspections", label: "Inspections" },
  { value: "sales", label: "Sales" },
];

const TYPE_LABELS = { inquiry: "Inquiry", quote: "Quote", inspection: "Inspection", contact: "Contact" };

const Overview = ({ range, refreshKey, onOpenLead }) => {
  const state = useReport("overview", range, refreshKey);
  const [metric, setMetric] = useState("leads");

  return (
    <ReportFrame state={state}>
      {(d) => {
        const cur = d.current;
        const prev = d.previous;
        const spark = (key) => d.daily.map((row) => row[key]);
        const tracking = d.trackingSince && new Date(d.trackingSince) <= new Date(d.range.from);
        const trackingNote = !d.trackingSince
          ? "Car views and searches are counted once the tracking update is live on both sites; until then they read 0."
          : !tracking
            ? `Car views and searches have been counted since ${fmtDate(d.trackingSince)}; earlier days in this period have none.`
            : "";
        const a = d.attention;
        const metricLabel = TREND_METRICS.find((m) => m.value === metric).label;
        const labels = periodLabels(d.window);
        return (
          <div className={styles.stack}>
            <div className={styles.attention}>
              <button
                type="button"
                className={styles.attentionItem}
                onClick={() => document.getElementById("da-needs-reply")?.scrollIntoView({ behavior: "smooth" })}
              >
                <span className={d.needsReply.count ? styles.dotCrit : styles.dotOk} />
                <strong>{fmtNum(d.needsReply.count)}</strong> leads waiting on a reply
              </button>
              <span className={styles.attentionItem}>
                <span className={a.inspectionsToAccept ? styles.dotCrit : styles.dotOk} />
                <strong>{fmtNum(a.inspectionsToAccept)}</strong> inspection requests to accept
                {a.oldestInspectionRequest && <span className={styles.muted}>· oldest {waitedFor(a.oldestInspectionRequest)}</span>}
              </span>
              <span className={styles.attentionItem}>
                <span className={styles.dotOk} style={{ background: "var(--da-muted)" }} />
                <strong>{fmtNum(a.waitingOnDealer)}</strong> waiting on a Japanese dealer
              </span>
              <span className={styles.attentionItem}>
                <span className={a.overdueInvoices ? styles.dotCrit : styles.dotOk} />
                <strong>{fmtNum(a.overdueInvoices)}</strong> overdue invoices
                {a.overdueInvoices > 0 && <span className={styles.muted}>· {fmtUsd(a.overdueAmount)} owed</span>}
              </span>
            </div>

            {trackingNote && <div className={styles.notice}>{trackingNote}</div>}

            <div className={styles.tiles}>
              <StatTile
                label="Car views"
                value={fmtNum(cur.views)}
                delta={<Delta current={cur.views} previous={prev.views} />}
                sub={`${fmtNum(cur.visitors)} visitors · ${fmtNum(cur.searches)} searches`}
                spark={spark("views")}
              />
              <StatTile
                label="Saves"
                value={fmtNum(cur.saves)}
                delta={<Delta current={cur.saves} previous={prev.saves} />}
                sub="cars saved to a garage"
                spark={spark("saves")}
              />
              <StatTile
                label="Leads"
                value={fmtNum(cur.leads)}
                delta={<Delta current={cur.leads} previous={prev.leads} />}
                sub="inquiries, quotes, inspection requests"
                spark={spark("leads")}
              />
              <StatTile
                label="Inspection requests"
                value={fmtNum(cur.inspections)}
                delta={<Delta current={cur.inspections} previous={prev.inspections} />}
                sub={`${fmtNum(a.inspectionsToAccept)} not yet accepted`}
                spark={spark("inspections")}
              />
              <StatTile
                label="Dealer sales"
                value={fmtNum(cur.sales)}
                delta={<Delta current={cur.salesValue} previous={prev.salesValue} suffix="in value" />}
                sub={`${fmtUsd(cur.salesValue)} · collected ${fmtUsd(cur.collected)}`}
                spark={spark("sales")}
              />
              <StatTile
                label="Median first reply"
                value={fmtHours(cur.medianReplyHours)}
                delta={<Delta current={cur.medianReplyHours} previous={prev.medianReplyHours} goodWhenDown />}
                sub={cur.repliedWithin24h === null ? "no leads answered yet" : `${cur.repliedWithin24h}% answered within 24 h`}
              />
            </div>

            <Card
              title={`${metricLabel} per day`}
              sub={`${labels.current} against the ${d.window.days} days before it, on the same scale. Days are US Eastern.`}
              actions={<Segmented label="Measure" options={TREND_METRICS} value={metric} onChange={setMetric} />}
            >
              <Legend items={[
                { label: labels.current, color: "var(--da-s1)", kind: "line" },
                { label: labels.previous, color: "var(--da-prev)", kind: "line" },
              ]} />
              <div style={{ marginTop: 10 }}>
                <TrendChart
                  points={d.daily.map((row) => ({ label: fmtDayKey(row.date), value: row[metric] }))}
                  previous={d.previousDaily.map((row) => ({ label: fmtDayKey(row.date), value: row[metric] }))}
                  format={fmtCompact}
                  currentLabel={metricLabel}
                  previousLabel="previous period"
                />
              </div>
            </Card>

            <div className={styles.grid2}>
              <Card
                title="Funnel"
                sub="How many of each, this period. Not one group moving down: most leads are guests who never saved a car."
              >
                <Funnel steps={[
                  { label: "Car views", value: cur.views },
                  { label: "Saves", value: cur.saves },
                  { label: "Leads", value: cur.leads },
                  { label: "Inspections", value: cur.inspections },
                  { label: "Sales", value: cur.sales },
                ]} />
              </Card>
              <Card
                title="How fast leads hear back"
                sub="Time to the team's first reply on the thread, or to the lead being marked contacted, whichever came first."
              >
                <BarList
                  rows={[
                    ...cur.reply.buckets.map((b) => ({ label: b.label, value: b.count })),
                    { label: "Not answered yet", value: cur.reply.unanswered },
                    { label: "Closed, no reply recorded", value: cur.reply.closedUnanswered },
                  ]}
                  empty="No leads in this period."
                />
                <p className={`${styles.small} ${styles.muted}`} style={{ marginTop: 12 }}>
                  {fmtNum(cur.reply.within1h)} of {fmtNum(cur.reply.answered)} answered leads heard back within an hour.
                </p>
              </Card>
            </div>

            <div id="da-needs-reply">
              <Card
                title={`Waiting on a reply (${fmtNum(d.needsReply.count)})`}
                sub="New leads nobody has answered, and threads where the customer wrote last. Oldest wait first."
              >
                {d.needsReply.items.length ? (
                  <SortTable
                    rowKey={(r) => r._id}
                    rows={d.needsReply.items}
                    columns={[
                      { key: "name", label: "Customer", render: (r) => <button type="button" className={styles.link} onClick={() => onOpenLead(r._id)}>{r.name}</button> },
                      { key: "vehicle", label: "Car", render: (r) => r.vehicle || <span className={styles.muted}>General</span> },
                      { key: "type", label: "Type", render: (r) => <span className={styles.chip}>{TYPE_LABELS[r.type] || r.type}</span> },
                      { key: "unread", label: "Unread", num: true },
                      { key: "waitingSince", label: "Waiting", num: true, sort: (r) => -new Date(r.waitingSince).getTime(), render: (r) => waitedFor(r.waitingSince) },
                    ]}
                  />
                ) : <Empty>Nobody is waiting on a reply.</Empty>}
              </Card>
            </div>
          </div>
        );
      }}
    </ReportFrame>
  );
};

export default Overview;
